import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { ReportDetailModal } from "../components/ReportDetailModal";
import { SponsorEditor } from "../components/SponsorEditor";
import { TeamManager } from "../components/TeamManager";
import { ModderApplications } from "../components/ModderApplications";
import { SupportersPanel } from "../components/SupportersPanel";
import { DispatchLog } from "../components/DispatchLog";
import { BulkImportPanel } from "../components/BulkImportPanel";
import { ClipScannerPanel } from "../components/ClipScannerPanel";
import { fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { X, Ban, Send, Loader2, Eye, ShieldCheck, Users, Lock, Flame, Trash2 } from "lucide-react";

const ACTIONS = [
  { key: "confirmed_cheater", label: "Cheater", icon: Ban, color: "#FF2A55" },
  { key: "rejected", label: "Reject", icon: X, color: "#64748B" },
];

const ModRow = ({ report, onAction, onOpen, onDelete, busy }) => {
  const isCommunity = report.report_type === "community";
  return (
    <div data-testid={`mod-row-${report.id}`} className="animate-fade-up rounded-xl border border-[#1E293F] bg-[#141B2D] p-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="h-24 w-full shrink-0 overflow-hidden rounded-lg bg-[#0F1424] sm:w-32">
          {report.images?.[0]
            ? <img src={fileUrl(report.images[0])} alt="" className="h-full w-full object-cover" />
            : <div className="flex h-full items-center justify-center text-xs text-[#334155]">No image</div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-lg font-bold text-white">{report.gamertag}</h3>
            <StatusBadge status={report.status} />
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${isCommunity ? "bg-[#F97316]/15 text-[#F97316]" : "bg-[#00F0FF]/15 text-[#00F0FF]"}`}>
              {isCommunity ? <><Users size={11} /> Community</> : <><Lock size={11} /> Direct</>}
            </span>
            {report.hot && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#F97316] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                <Flame size={11} /> Hot
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-[#94A3B8]">
            <span className="rounded bg-[#0F1424] px-1.5 py-0.5 font-mono text-[#00F0FF]">{report.platform}</span>
            <span className="rounded bg-[#0F1424] px-1.5 py-0.5">{report.game_mode}</span>
            <span className="rounded border border-[#FF2A55]/30 bg-[#FF2A55]/10 px-1.5 py-0.5 text-[#FF2A55]">{report.cheat_type}</span>
            <span className="rounded bg-[#0F1424] px-1.5 py-0.5 text-[#FF2A55]">▲ {report.upvotes} faced</span>
            {isCommunity && <span className="rounded bg-[#0F1424] px-1.5 py-0.5 text-[#00FF87]">▼ {report.downvotes} legit</span>}
          </div>
          <p className="mt-2 line-clamp-2 text-sm text-[#94A3B8]">{report.description}</p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button data-testid={`mod-view-${report.id}`} size="sm" variant="outline" onClick={() => onOpen(report)}
              className="border-[#1E293F] bg-[#0F1424] text-white hover:bg-[#1A233A] hover:text-white">
              <Eye size={14} className="mr-1" /> Inspect
            </Button>
            {ACTIONS.filter((a) => a.key !== report.status).map((a) => (
              <Button
                key={a.key}
                data-testid={`mod-${a.key}-${report.id}`}
                size="sm"
                disabled={busy}
                onClick={() => onAction(report, a.key)}
                className="font-semibold"
                style={{ backgroundColor: `${a.color}22`, color: a.color, border: `1px solid ${a.color}44` }}
              >
                <a.icon size={14} className="mr-1" /> {a.label}
              </Button>
            ))}
            <Button data-testid={`mod-delete-${report.id}`} size="sm" disabled={busy} onClick={() => onDelete(report)}
              className="font-semibold" style={{ backgroundColor: "#FF2A5522", color: "#FF2A55", border: "1px solid #FF2A5544" }}>
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, isMod, loading: authLoading } = useAuth();
  const [pending, setPending] = useState([]);
  const [vetted, setVetted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [cheaterTarget, setCheaterTarget] = useState(null);
  const [cheaterNote, setCheaterNote] = useState("");
  const [clearing, setClearing] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v] = await Promise.all([
        api.get("/reports/pending"),
        api.get("/reports", { params: { status: "all", sort: "recent" } }),
      ]);
      setPending(p.data);
      setVetted(v.data);
    } catch {
      toast.error("Failed to load the queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isMod) load();
  }, [isMod, load]);

  if (!authLoading && (!user || !isMod)) return <Navigate to="/" replace />;

  const doAction = async (report, status, note) => {
    // "Cheater" and "Reject" open a dialog to capture the mod's note.
    if (status === "rejected" && note === undefined) {
      setRejectTarget(report); setRejectNote(""); return;
    }
    if (status === "confirmed_cheater" && note === undefined) {
      setCheaterTarget(report); setCheaterNote(""); return;
    }
    setBusy(true);
    try {
      await api.patch(`/reports/${report.id}/status`, { status, note: note ?? null });
      toast.success(`Marked "${report.gamertag}" as ${status.replace("_", " ")}.`);
      await load();
    } catch {
      toast.error("Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const confirmReject = async () => {
    const target = rejectTarget;
    setRejectTarget(null);
    await doAction(target, "rejected", rejectNote.trim());
  };

  const confirmCheater = async () => {
    const target = cheaterTarget;
    setCheaterTarget(null);
    await doAction(target, "confirmed_cheater", cheaterNote.trim());
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete the post on "${report.gamertag}"? This permanently removes it and can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/reports/${report.id}`);
      toast.success("Post deleted.");
      setSelected(null);
      await load();
    } catch {
      toast.error("Could not delete the post.");
    } finally {
      setBusy(false);
    }
  };

  const clearSeed = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/admin/clear-seed-data");
      toast.success(data.deleted > 0 ? `Removed ${data.deleted} sample report(s).` : "No sample reports found to remove.");
      await load();
    } catch {
      toast.error("Could not clear sample data.");
    } finally {
      setClearing(false);
    }
  };

  const dispatchDigest = async () => {
    setDispatching(true);
    try {
      const { data } = await api.post("/admin/dispatch-digest");
      if (data.sent) toast.success(`Weekly digest sent to 2K — ${data.count} case(s).`);
      else toast.info(data.detail || "Nothing to dispatch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the 2K digest.");
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="animate-fade-up flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#00F0FF]">
            <ShieldCheck size={13} /> Modder Workspace
          </div>
          <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">Vetting Queue</h1>
          <p className="mt-2 text-[#94A3B8]">Review the evidence, then mark confirmed cheaters or reject with a reason. Confirmed cheaters are automatically included in the nightly dossier sent to 2K.</p>
        </div>
        {isMod && (
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="dispatch-digest-btn" variant="outline" size="sm" disabled={dispatching} onClick={dispatchDigest}
              className="border-[#00FF87]/40 bg-[#00FF87]/10 text-[#00FF87] hover:bg-[#00FF87]/20 hover:text-[#00FF87]">
              {dispatching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send size={14} className="mr-1" />} Send 2K digest now
            </Button>
            {isAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="clear-seed-btn" variant="outline" size="sm" disabled={clearing}
                    className="border-[#FF2A55]/40 bg-[#FF2A55]/10 text-[#FF2A55] hover:bg-[#FF2A55]/20 hover:text-[#FF2A55]">
                    {clearing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 size={14} className="mr-1" />} Clear sample data
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-[#1E293F] bg-[#0F1424] text-white" data-testid="clear-seed-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove sample reports?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#94A3B8]">
                      This deletes the demo/generic "Community Report" entries that were seeded for testing. Real user submissions are not affected. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="clear-seed-confirm" onClick={clearSeed} className="bg-[#FF2A55] text-white hover:bg-[#FF2A55]/90">Delete sample data</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      <div className="mt-8"><ModderApplications /></div>
      <TeamManager />
      <SupportersPanel />
      {isMod && <DispatchLog />}
      <ClipScannerPanel onCreated={load} />
      <BulkImportPanel />
      {isAdmin && <SponsorEditor />}

      {loading ? (
        <div className="flex justify-center py-24 text-[#64748B]"><Loader2 className="animate-spin" /></div>
      ) : (
        <Tabs defaultValue="pending" className="mt-8">
          <TabsList className="bg-[#0F1424] border border-[#1E293F]">
            <TabsTrigger data-testid="tab-pending" value="pending" className="data-[state=active]:bg-[#FFB800]/15 data-[state=active]:text-[#FFB800] text-[#94A3B8]">
              Queue ({pending.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-vetted" value="vetted" className="data-[state=active]:bg-[#00F0FF]/15 data-[state=active]:text-[#00F0FF] text-[#94A3B8]">
              On the Wall ({vetted.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-5 space-y-4">
            {pending.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#1E293F] bg-[#0F1424] py-16 text-center text-[#94A3B8]" data-testid="empty-pending">
                All caught up. No reports awaiting review.
              </div>
            ) : pending.map((r) => (
              <ModRow key={r.id} report={r} busy={busy} onAction={doAction} onOpen={setSelected} onDelete={handleDelete} />
            ))}
          </TabsContent>

          <TabsContent value="vetted" className="mt-5 space-y-4">
            {vetted.map((r) => (
              <ModRow key={r.id} report={r} busy={busy} onAction={doAction} onOpen={setSelected} onDelete={handleDelete} />
            ))}
          </TabsContent>
        </Tabs>
      )}

      <ReportDetailModal report={selected} open={!!selected} onClose={() => setSelected(null)} onUpvote={() => {}} onDelete={handleDelete} />

      <Dialog open={!!cheaterTarget} onOpenChange={(v) => !v && setCheaterTarget(null)}>
        <DialogContent className="border-[#1E293F] bg-[#0F1424] text-white" data-testid="cheater-dialog">
          <DialogHeader>
            <DialogTitle>Confirm {cheaterTarget?.gamertag} as a cheater</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">Explain why you believe they're cheating. This is shown publicly on the case and included in the 2K dossier.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="cheater-note-input"
            value={cheaterNote}
            onChange={(e) => setCheaterNote(e.target.value)}
            rows={4}
            placeholder="e.g. Perfect green on every jumpshot regardless of contest/timing, identical release frame all game — clear auto-green macro. Steals show inhuman reaction time."
            className="border-[#1E293F] bg-[#141B2D] text-white placeholder:text-[#64748B]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheaterTarget(null)} className="border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white">Cancel</Button>
            <Button data-testid="cheater-confirm-btn" disabled={busy} onClick={confirmCheater} className="bg-[#FF2A55] font-bold text-white hover:bg-[#FF2A55]/90">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm Cheater
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent className="border-[#1E293F] bg-[#0F1424] text-white" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject report on {rejectTarget?.gamertag}</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">Add a reason (optional). The reporter will see this note on their report.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="reject-note-input"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            rows={3}
            placeholder="e.g. Evidence is inconclusive — the clip doesn't clearly show cheating."
            className="border-[#1E293F] bg-[#141B2D] text-white placeholder:text-[#64748B]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white">Cancel</Button>
            <Button data-testid="reject-confirm-btn" disabled={busy} onClick={confirmReject} className="bg-[#FF2A55] font-bold text-white hover:bg-[#FF2A55]/90">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reject report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
